/**
 * „Alltag starten" und „Alltag beenden" per Zuruf. (01.10.2026, Ticket 2849)
 *
 * Martin: *„bitte meinen Alltag starten“* — die Aufnahme läuft dann im
 * Hintergrund; ebenso „Alltag beenden“. Bis heute gab es die Daueraufnahme nur
 * als Schalter (Zuhause-Karte, Einstellungen).
 *
 * ## Der Weg
 *
 *   Werkzeug `alltag_steuern` (tools/alltag-steuern.ts)
 *     → Server schickt der App einen AUFTRAG (SSE-Ereignis `auftrag`,
 *       server/geraete-auftrag.ts)
 *     → die App tut dasselbe wie der Schalter „Durchgehend“
 *       (frontend/lib/alltag-steuern.ts)
 *     → die App ANTWORTET mit dem Zustand, den sie danach selbst misst
 *       (POST /api/geraet/auftrag-antwort)
 *     → erst dann sagt das Werkzeug „läuft“.
 *
 * ⚠️ Das Werkzeug behauptet nie, was die App nicht bestätigt hat („Anzeige
 *    behauptet zuviel“): kein Gerät erreichbar, keine Antwort, oder eine
 *    Antwort „läuft nicht, weil …“ — jedes davon wird so gesagt.
 *
 * ⚠️ Datenschutz: die Tagesaufnahme nimmt die Umgebung auf. Darum nur Konto 1,
 *    STARTEN nur auf Zuruf aus der App (nie im Anruf, aus Telegram, geplanten
 *    Aufträgen oder Hintergrundläufen: dort gibt es keinen Schutz vor
 *    eingeschleusten Anweisungen), nur auf einem Gerät, dessen App sichtbar
 *    ist, und mit der gewohnten Anzeige (Zuhause-Karte, iOS-Punkt).
 *    Beenden geht immer, auch aus dem Anruf.
 *
 * ⚠️ Die Namen stehen HIER, weil Werkzeug, Server und App sie brauchen.
 */

export const ALLTAG_STEUERN = "alltag_steuern";

export const ALLTAG_AKTIONEN = ["start", "stop"] as const;
export type AlltagAktion = (typeof ALLTAG_AKTIONEN)[number];

export function istAlltagAktion(x: unknown): x is AlltagAktion {
  return typeof x === "string" && (ALLTAG_AKTIONEN as readonly string[]).includes(x);
}

/** Der Name des SSE-Ereignisses (neben `message`, `ready`, `ping`). */
export const AUFTRAG_EREIGNIS = "auftrag";

/** Mit diesem Wert in `?kann=` meldet sich eine Seite, die den Alltag steuern kann (nur die iPhone-App). */
export const KANN_ALLTAG = "alltag";

/** Wie lange das Werkzeug auf die Antwort der App wartet. */
export const AUFTRAG_WARTEN_MS = 8_000;

/**
 * Längstens so lange arbeitet die App an EINEM Auftrag, bevor der nächste dran ist —
 * ein hängender Start darf spätere Stopps nicht blockieren.
 */
export const AUFTRAG_LAUF_MAX_MS = 10_000;

/** Was die App der Antwort als Grund mitgibt — feste Kennungen, nie ein freier Text. */
const AUFTRAG_GRUENDE = [
  "nicht-sichtbar",
  "nur-iphone-app",
  "manuell-pausiert",
  "gerade-nicht-moeglich",
  "kein-mikro-recht",
  "start-gescheitert",
  "stopp-gescheitert",
  "unbekannte-aktion",
] as const;
export type AuftragGrund = (typeof AUFTRAG_GRUENDE)[number];

/** Der Zustand der Aufnahme, so wie ihn die App NACH der Handlung misst. */
const ALLTAG_ZUSTAENDE = ["laeuft", "pausiert", "aus"] as const;
export type AlltagZustand = (typeof ALLTAG_ZUSTAENDE)[number];

/** Der Auftrag, den der Server der App schickt. */
export interface GeraeteAuftrag {
  id: string;
  art: "alltag";
  aktion: AlltagAktion;
  /**
   * Bis wann der Auftrag gilt (ms, Zeit des Servers beim Senden plus Frist). Die App verwirft einen
   * abgelaufenen Auftrag vor jeder Handlung: ein Auftrag, der Stunden später eintrifft (schlafendes
   * Gerät, gepufferter Kanal), startet nie eine Aufnahme.
   */
  gueltigBis: number;
}

/** Die Antwort der App. `ausgefuehrt: false` heißt: die App hat nichts getan (Grund steht dabei). */
export interface AuftragsAntwort {
  ausgefuehrt: boolean;
  /** Was die App danach misst; bei `ausgefuehrt: false` der Zustand, der unverändert blieb. */
  zustand: AlltagZustand;
  grund?: AuftragGrund;
  /** Warum die Aufnahme pausiert — kurze Wörter („Anruf“, „Sprechtaste“), höchstens 3. */
  pausenGruende?: string[];
}

const ID_FORM = /^[a-f0-9]{16,64}$/;

function istAuftragsId(x: unknown): x is string {
  return typeof x === "string" && ID_FORM.test(x);
}

/** Ein Auftrag aus einem SSE-Frame — nur die erwartete Form, sonst null. */
export function leseAuftrag(roh: unknown): GeraeteAuftrag | null {
  if (!roh || typeof roh !== "object") return null;
  const r = roh as Record<string, unknown>;
  if (!istAuftragsId(r.id) || r.art !== "alltag" || !istAlltagAktion(r.aktion)) return null;
  if (typeof r.gueltigBis !== "number" || !Number.isFinite(r.gueltigBis)) return null;
  return { id: r.id, art: "alltag", aktion: r.aktion, gueltigBis: r.gueltigBis };
}

/** Kurze Wörter ohne Steuerzeichen, höchstens drei, je 24 Zeichen. */
function saubereGruende(x: unknown): string[] {
  if (!Array.isArray(x)) return [];
  return x
    .filter((g): g is string => typeof g === "string")
    // eslint-disable-next-line no-control-regex
    .map((g) => g.replace(/[\u0000-\u001f\u007f]/g, " ").trim().slice(0, 24))
    .filter((g) => g.length > 0)
    .slice(0, 3);
}

/** Die Antwort der App, geprüft — null, wenn sie nicht die erwartete Form hat. */
export function leseAntwort(roh: unknown): AuftragsAntwort | null {
  if (!roh || typeof roh !== "object") return null;
  const r = roh as Record<string, unknown>;
  if (typeof r.ausgefuehrt !== "boolean") return null;
  if (!(ALLTAG_ZUSTAENDE as readonly string[]).includes(r.zustand as string)) return null;
  const grund = (AUFTRAG_GRUENDE as readonly string[]).includes(r.grund as string)
    ? (r.grund as AuftragGrund)
    : undefined;
  const pausenGruende = saubereGruende(r.pausenGruende);
  return {
    ausgefuehrt: r.ausgefuehrt,
    zustand: r.zustand as AlltagZustand,
    ...(grund ? { grund } : {}),
    ...(pausenGruende.length > 0 ? { pausenGruende } : {}),
  };
}

/** Der Satz zu einem Grund — ehrlich und kurz. */
export const GRUND_SATZ: Readonly<Record<AuftragGrund, string>> = {
  "nicht-sichtbar": "die App ist gerade nicht geöffnet im Vordergrund",
  "nur-iphone-app": "das geht nur in der iPhone-App",
  "manuell-pausiert": "er die Aufnahme selbst pausiert hat",
  "gerade-nicht-moeglich": "die Aufnahme gerade nicht gestartet werden kann (sie beendet noch eine Sitzung oder ist gestört); ein neuer Versuch in einem Moment geht",
  "kein-mikro-recht": "die App hat keine Erlaubnis für das Mikrofon",
  "start-gescheitert": "die Aufnahme ließ sich nicht starten",
  "stopp-gescheitert": "die Aufnahme ließ sich nicht beenden",
  "unbekannte-aktion": "die App kannte den Auftrag nicht",
};
